import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NOTIFICATION_VARIABLES,
  indianMobileSchema,
  type EventMapping,
  type MessagePreview,
  type MessageTemplate,
  type NotificationVariableKey,
  type UpdateEventMappingInput,
  type VariableBinding,
} from '@tailoring-crm/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@tailoring-crm/ui';

/**
 * Mapping one notification to one of the shop's own templates.
 *
 * The screen is driven by the CHOSEN TEMPLATE, not by the event: a WhatsApp template is a
 * sentence with numbered holes in it, so once one is picked the form shows exactly those holes
 * and asks what goes in each. Every field on offer comes from the event's own documented list,
 * which is why "payment amount" is simply not offered on "order booked" — the alternative is a
 * dropdown of everything and a message that renders a dash forever.
 *
 * The preview underneath is the whole point of the dialog. Nobody can read `{{1}}, {{2}}` and
 * know whether they got it right; everybody can read "Hello Lakshmi, your order ORD-2026-0042
 * is ready". The test-send row below it is the same idea, one step further.
 */
export interface EventMappingDialogProps {
  mapping: EventMapping;
  templates: readonly MessageTemplate[];
  templatesError: string | null;
  preview: MessagePreview | undefined;
  saving: boolean;
  testing: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (patch: UpdateEventMappingInput) => Promise<void>;
  onTestSend: (to: string) => Promise<void>;
}

export function EventMappingDialog({
  mapping,
  templates,
  templatesError,
  preview,
  saving,
  testing,
  onOpenChange,
  onSave,
  onTestSend,
}: EventMappingDialogProps): ReactElement {
  const { t } = useTranslation();

  const [templateName, setTemplateName] = useState(mapping.templateName ?? '');
  const [bindings, setBindings] = useState<VariableBinding[]>([...mapping.bindings]);
  const [testNumber, setTestNumber] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reopening on another event must not carry the previous one's answers over.
  useEffect(() => {
    setTemplateName(mapping.templateName ?? '');
    setBindings([...mapping.bindings]);
    setError(null);
  }, [mapping]);

  const template = templates.find((candidate) => candidate.name === templateName) ?? null;
  const placeholders = template?.placeholders ?? [];

  const bindingFor = (position: number): VariableBinding =>
    bindings.find((binding) => binding.position === position) ?? {
      position,
      variable: null,
      literal: null,
    };

  const setBinding = (position: number, variable: NotificationVariableKey | null): void => {
    setBindings((previous) => [
      ...previous.filter((binding) => binding.position !== position),
      { position, variable, literal: null },
    ]);
  };

  /** Only the placeholders this template actually has — a stale binding must not be saved. */
  const bindingsToSave = useMemo(
    () => placeholders.map((position) => bindingFor(position)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placeholders.join(','), bindings],
  );

  const save = async (patch: UpdateEventMappingInput): Promise<void> => {
    setError(null);
    try {
      await onSave(patch);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  const testSend = async (): Promise<void> => {
    const parsed = indianMobileSchema.safeParse(testNumber);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? null);
      return;
    }
    setError(null);
    try {
      await onTestSend(parsed.data);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{mapping.label}</DialogTitle>
          <DialogDescription>{mapping.description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          ) : null}
          {templatesError ? (
            <p role="alert" className="text-sm text-warning">
              {templatesError}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mapping-template">{t('notifications.template')}</Label>
            <select
              id="mapping-template"
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              <option value="">{t('notifications.chooseTemplate')}</option>
              {templates.map((candidate) => (
                <option key={`${candidate.name}:${candidate.language}`} value={candidate.name}>
                  {candidate.name} ({candidate.language})
                  {candidate.status === 'approved' ? '' : ` — ${candidate.status}`}
                </option>
              ))}
            </select>
          </div>

          {template ? (
            <section className="flex flex-col gap-3">
              <h4 className="text-sm font-semibold">{t('notifications.bindingsHeading')}</h4>
              {placeholders.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('notifications.noPlaceholders')}</p>
              ) : (
                placeholders.map((position) => (
                  <div key={position} className="flex flex-col gap-1.5">
                    <Label htmlFor={`binding-${String(position)}`}>
                      {t('notifications.placeholder', { position })}
                    </Label>
                    <select
                      id={`binding-${String(position)}`}
                      value={bindingFor(position).variable ?? ''}
                      onChange={(event) =>
                        setBinding(
                          position,
                          event.target.value === ''
                            ? null
                            : (event.target.value as NotificationVariableKey),
                        )
                      }
                      className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                    >
                      <option value="">{t('notifications.bindNothing')}</option>
                      {mapping.availableVariables.map((variable) => (
                        <option key={variable} value={variable}>
                          {NOTIFICATION_VARIABLES[variable].label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))
              )}
            </section>
          ) : null}

          {preview ? (
            <section className="flex flex-col gap-2">
              <h4 className="text-sm font-semibold">{t('notifications.previewHeading')}</h4>
              <p className="whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 text-sm">
                {preview.rendered || t('notifications.previewEmpty')}
              </p>
              {preview.problems.length > 0 ? (
                <ul className="flex flex-col gap-1 text-sm text-warning">
                  {preview.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}

          <section className="flex flex-col gap-1.5">
            <Label htmlFor="test-number">{t('notifications.testSendLabel')}</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="test-number"
                inputMode="numeric"
                value={testNumber}
                onChange={(event) => setTestNumber(event.target.value)}
                placeholder={t('notifications.testSendPlaceholder')}
              />
              <Button
                variant="outline"
                disabled={testing || !mapping.templateName}
                onClick={() => void testSend()}
              >
                {t('notifications.testSend')}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">{t('notifications.testSendHint')}</p>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          {mapping.enabled ? (
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => void save({ enabled: false })}
            >
              {t('notifications.turnOff')}
            </Button>
          ) : null}
          <Button
            disabled={saving}
            onClick={() =>
              void save({
                templateName: templateName === '' ? null : templateName,
                templateLanguage: template?.language ?? null,
                bindings: bindingsToSave,
                // Saving a mapping is how a shop switches the notification on; leaving it off
                // would mean configuring something carefully and it still doing nothing.
                enabled: templateName !== '',
              })
            }
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
